import React from 'react';
import { Avatar, Grid, Paper, IconButton, Tooltip, CircularProgress } from '@mui/material';
import { styled } from '@mui/system';
import {
  FileCopyOutlined as FileCopyIcon,
  ThumbUpOutlined as ThumbUpIcon,
  ThumbDownOutlined as ThumbDownIcon,
  ThumbUp as ThumbUpFilledIcon,
  ThumbDown as ThumbDownFilledIcon,
} from '@mui/icons-material';
import { ReactMarkdown } from 'react-markdown/lib/react-markdown';
import remarkGfm from 'remark-gfm';
import { remarkExtendedTable, extendedTableHandlers } from 'remark-extended-table';
import { api } from '../services/api';
import ResponseIcon from '@mui/icons-material/Psychology';
import ErrorIcon from '@mui/icons-material/Error';
import { recordEvent } from '../utils/analytics-wrapper';
import CustomImage from './CustomImage';

const style = {
  message: {
    wordBreak: 'break-word',
    paddingTop: '0px',
    textAlign: 'left',
    maxWidth: 'inherit',
  },
  markdown: {
    width: 'max-content',
    wordBreak: 'break-word',
  },
};

const ChatItem = ({
  content,
  sources,
  footer,
  showActions,
  position,
  user,
  color,
  id,
  salt,
  uuid,
  background,
  date,
  loading,
  cancel,
  error,
  logger,
  anonGroup,
  remFontSize,
  customResponseImageUrl
}) => {
  const [liked, setLiked] = React.useState(false);
  const [disliked, setDisliked] = React.useState(false);
  const copyText = () => {
    recordEvent(
      {
        name: 'copyMessage',
        metrics: { messageLength: content.length }, // Character count
      },
      logger,
    );
    navigator.clipboard.writeText(content);
  };

  const StyledPaper = styled(Paper)(() => ({
    backgroundColor: background,
    color: 'black',
    borderRadius: '1rem',
    overflowX: 'auto',    // only show horizontal scrollbar when needed
    overflowY: 'hidden',  // never show vertical scrollbar
    paddingTop: 0,
    fontSize: `${remFontSize}rem`
  }));

  const like = () => {
    if (liked) {
      api.restartLike(id, salt, uuid, anonGroup);
      setLiked(false);
      setDisliked(false);
    } else {
      api.likeMessage(id, salt, uuid, anonGroup);
      setLiked(true);
      setDisliked(false);
      recordEvent(
        {
          name: 'likeMessage',
          metrics: { messageLength: content.length }, // Character count
        },
        logger,
      );
    }
  };

  const dislike = () => {
    if (disliked) {
      api.restartLike(id, salt, uuid, anonGroup);
      setDisliked(false);
      setLiked(false);
    } else {
      api.dislikeMessage(id, salt, uuid, anonGroup);
      setDisliked(true);
      setLiked(false);
      recordEvent(
        {
          name: 'dislikeMessage',
          metrics: { messageLength: content.length }, // Character count
        },
        logger,
      );
    }
  };

  const direction = position === 'left' ? 'row' : 'row-reverse';
  return (
    <Grid container spacing={2} style={{ flexFlow: direction }} alignItems="flex-start">
      {user && (
        <Grid item>
          <Avatar variant="rounded" style={{ backgroundColor: color, float: position, marginTop: '6px', height: customResponseImageUrl ? '60px' : undefined, width: customResponseImageUrl ? '60px' : undefined }}>
            {error ? (
              <ErrorIcon style={{ height: 35, width: 35 }} />
            ) : customResponseImageUrl ? (
              <CustomImage imageUrl={customResponseImageUrl} fontSize="large" />
            ) : (
              <ResponseIcon fontSize="large" />
            )}
          </Avatar>
        </Grid>
      )}
      <Grid item style={{ maxWidth: '90%', width: error ? '100%' : null }}>
        <StyledPaper
          sx={{
            my: 1,
            mx: 'auto',
            p: 2,
          }}
        >
          <Grid container alignItems="center" wrap="wrap">
            {position === 'left' && content === '' ? (
              <Grid item style={{ ...style.message }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <CircularProgress style={{ height: 30, width: 30 }} />{' '}
                  <span style={{ marginLeft: 10, marginRight: 10 }}>
                    Processing. Please wait...
                  </span>
                </div>
              </Grid>
            ) : (
              <>
                <Grid item xs={12} style={{ fontSize: 11, color: error ? '#BA1A1A' : '#41484D' }}>
                  <span>{date + '\n'}</span>
                </Grid>
                <Grid item style={style.message}>
                  <ReactMarkdown
                    style={style.markdown}
                    remarkPlugins={[remarkGfm, extendedTableHandlers, remarkExtendedTable]}
                    breaks={true}
                    linkTarget="_blank"
                  >
                    {position === 'left' ? content : content.replace(/\n/g, '\n\n')}
                  </ReactMarkdown>
                  {sources}
                  {footer}
                </Grid>
              </>
            )}
            {/* actions should be copy on the left and up and down thumbs on the right */}
            {showActions && !cancel && content !== '' && (
              <Grid id="conversation-icons" container justifyContent="space-between">
                <Grid item>
                  <Tooltip title="Copy to clipboard">
                    <span>
                      <IconButton
                        aria-label="copy"
                        onClick={copyText}
                        disabled={loading}
                        style={{
                          color: loading ? 'rgba(0, 0, 0, 0.26)' : error ? '#BA1A1A' : 'secondary',
                        }}
                      >
                        <FileCopyIcon
                          color={loading ? 'rgba(0, 0, 0, 0.26)' : error ? '#BA1A1A' : 'secondary'}
                        />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {!error && (
                    <>
                      <Tooltip title="Flag response as good or helpful">
                        <span>
                          <IconButton aria-label="like" onClick={like} disabled={loading}>
                            {liked ? (
                              <ThumbUpFilledIcon
                                color={loading ? 'rgba(0, 0, 0, 0.26)' : 'secondary'}
                              />
                            ) : (
                              <ThumbUpIcon color={loading ? 'rgba(0, 0, 0, 0.26)' : 'secondary'} />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Flag response as bad or unhelpful">
                        <span>
                          <IconButton aria-label="dislike" onClick={dislike} disabled={loading}>
                            {disliked ? (
                              <ThumbDownFilledIcon
                                color={loading ? 'rgba(0, 0, 0, 0.26)' : 'secondary'}
                              />
                            ) : (
                              <ThumbDownIcon
                                color={loading ? 'rgba(0, 0, 0, 0.26)' : 'secondary'}
                              />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </>
                  )}
                </Grid>
              </Grid>
            )}
          </Grid>
        </StyledPaper>
      </Grid>
    </Grid>
  );
};

export default ChatItem;
